import React, { useState } from 'react';
import { queueTaskAssignedNotification } from '../../services/notificationService';
import { X } from 'lucide-react';

const TaskDetailPanel = ({ task, project, onClose, onUpdate, teamMembers }) => {
  const [userNotes, setUserNotes] = useState(task.userNotes || '');
  const [taskStatus, setTaskStatus] = useState(task.status);
  const [assignedTo, setAssignedTo] = useState(task.assignedTo || '');
  const [dueDate, setDueDate] = useState(task.dueDate || '');
  const [scheduledDate, setScheduledDate] = useState(task.scheduledDate || '');
  const [scheduledTime, setScheduledTime] = useState(task.scheduledTime || '');

  const handleSave = () => {
    const previousAssignee = task.assignedTo || '';
    const newAssignee = assignedTo || '';

    onUpdate({ userNotes, status: taskStatus, assignedTo, dueDate, scheduledDate, scheduledTime });

    // Fire notification if assignee changed to someone new
    if (newAssignee && newAssignee !== previousAssignee) {
      const updatedTask = { ...task, assignedTo, dueDate, status: taskStatus, theme: task.theme };
      queueTaskAssignedNotification(project, updatedTask, teamMembers);
    }

    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onKeyDown={handleKeyDown}>
      <div className="bg-white rounded-xl shadow-2xl flex flex-col w-full max-w-lg mx-4" style={{ maxHeight: '90vh' }}>
      <div className="p-4 border-b flex items-center justify-between">
        <h3 className="font-bold text-lg">Task Details</h3>
        <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div>
          <label className="block text-sm font-semibold mb-2">Task Name</label>
          <div className="text-gray-700">{task.name}</div>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Assigned To</label>
          <select
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Unassigned</option>
            {teamMembers.map((member) => (
              <option key={member.id} value={`${member.firstName} ${member.lastName}`}>
                {member.firstName} {member.lastName}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Status</label>
          <select
            value={taskStatus}
            onChange={(e) => setTaskStatus(e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="not-started">Not Started</option>
            <option value="in-progress">In Progress</option>
            <option value="complete">Complete</option>
            <option value="not-applicable">Not Applicable (N/A)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Due Date</label>
          <input
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            className="w-full p-2 border rounded"
          />
          {dueDate && (() => {
            const today = new Date(); today.setHours(0,0,0,0);
            const due = new Date(dueDate); due.setHours(0,0,0,0);
            const diff = Math.ceil((due - today) / (1000*60*60*24));
            if (taskStatus === 'complete') return null;
            if (diff < 0) return <p style={{ fontSize: 12, color: '#DC2626', marginTop: 4, fontWeight: 600 }}>⚠ Overdue by {Math.abs(diff)} day{Math.abs(diff) !== 1 ? 's' : ''}</p>;
            if (diff <= 3) return <p style={{ fontSize: 12, color: '#92400E', marginTop: 4, fontWeight: 600 }}>⏳ Due in {diff} day{diff !== 1 ? 's' : ''}</p>;
            return null;
          })()}
        </div>
        {(task.isAppointment || task.name?.toLowerCase().includes('schedule')) && (
          <div style={{ backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: 12 }}>
            <label className="block text-sm font-semibold mb-2" style={{ color: '#1D4ED8' }}>📅 Schedule Appointment</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Date</label>
                <input
                  type="date"
                  value={scheduledDate}
                  onChange={e => setScheduledDate(e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Time</label>
                <input
                  type="time"
                  value={scheduledTime}
                  onChange={e => setScheduledTime(e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
              {scheduledDate && (
                <p style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 600 }}>
                  📌 Scheduled: {scheduledDate}{scheduledTime ? ` at ${scheduledTime}` : ''}
                </p>
              )}
            </div>
          </div>
        )}
        <div>
          <label className="block text-sm font-semibold mb-2">Instructions</label>
          <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{task.notes || 'No instructions provided'}</div>
        </div>
        <div>
          <label className="block text-sm font-semibold mb-2">Your Notes</label>
          <textarea
            value={userNotes}
            onChange={(e) => setUserNotes(e.target.value)}
            className="w-full p-2 border rounded h-32"
            placeholder="Add your notes here..."
          />
        </div>
      </div>
      <div className="p-4 border-t">
        <button
          onClick={handleSave}
          className="w-full text-white p-3 rounded font-semibold" style={{ backgroundColor: '#75BB2E' }}
        >
          Save Changes
        </button>
      </div>
    </div>
    </div>
  );
};

export default TaskDetailPanel;
